import {
  NavigationGuardReturn,
  RouteLocationNormalized,
} from 'vue-router'
import { Capacitor } from '@capacitor/core'

import { READONLY_MODE, READWRITE_MODE, ROUTE_NAME } from '@/const'

import { useNotifications } from '@/plugins/notification'

import { prepareResponseError } from '@/store/utils/helpers'

import { useUserStore } from '@/store/user'
import { useUserSubscriptionsStore } from '@/store/user/subscriptions'
import { useSettingsStore } from '@/store/settings'

// Vue Router 5 deprecated the `next()` callback in navigation guards — return
// the value (or undefined to continue) instead. See vue-router v5 migration.

export async function requireAuth(
  to: RouteLocationNormalized,
): Promise<NavigationGuardReturn> {
  const { warn } = useNotifications()
  const userStore = useUserStore()
  const userSubscriptionsStore = useUserSubscriptionsStore()
  try {
    await userStore.auth()
    if (!userStore.isAuthenticated) {
      throw 'You need to be authorized'
    }
    if (
      userSubscriptionsStore.isInactive &&
      to.name !== ROUTE_NAME.SUBSCRIPTION
    ) {
      return { name: ROUTE_NAME.SUBSCRIPTION }
    }
    return undefined
  } catch (e) {
    const message = prepareResponseError(e)
    await warn({ message })
    return { name: ROUTE_NAME.SIGNIN, query: { path: to.path }, hash: to.hash }
  }
}

export async function isAlreadyAuth(): Promise<NavigationGuardReturn> {
  const userStore = useUserStore()
  const userSubscriptionsStore = useUserSubscriptionsStore()
  try {
    await userStore.auth()
    if (userStore.isAuthenticated) {
      const entryRoute = userSubscriptionsStore.isInactive
        ? ROUTE_NAME.SUBSCRIPTION
        : ROUTE_NAME.DASHBOARDS
      return { name: entryRoute }
    }
    return undefined
  } catch {
    return undefined
  }
}

export async function checkSignupIsAllowed(
  to: RouteLocationNormalized,
): Promise<NavigationGuardReturn> {
  if (!__IS_PROD__ || !!to.query.invite_id) {
    return undefined
  }
  return '/404'
}

export async function redirectToSignin(
  to: RouteLocationNormalized,
): Promise<NavigationGuardReturn> {
  if (
    Capacitor.isNativePlatform() ||
    window.location.origin.includes('//app.')
  ) {
    return { name: ROUTE_NAME.SIGNIN, query: { path: to.path }, hash: to.hash }
  }
  return undefined
}

export async function deniedIfReadonly(): Promise<NavigationGuardReturn> {
  // @ts-expect-error - window is a global object
  if (window[READONLY_MODE]) {
    return { name: ROUTE_NAME.PERMISSION_DENIED }
  }
  return undefined
}

export async function deniedIfReadWrite(): Promise<NavigationGuardReturn> {
  // @ts-expect-error - window is a global object
  if (window[READWRITE_MODE]) {
    return { name: ROUTE_NAME.PERMISSION_DENIED }
  }
  return undefined
}

export async function requireAnalyticsDebug(): Promise<NavigationGuardReturn> {
  const settingsStore = useSettingsStore()
  if (settingsStore.getPowerMode) {
    return undefined
  }
  return '/404'
}
